import { Box, List, ListItemButton, ListItemText, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { useGetBusinesses } from 'apis/agency/hook';
import { BusinessProfileResponse } from 'apis/business/types';
import { useGetCurrentUser } from 'apis/user/hook';
import BackButtonIcon from 'assets/icons/BackButtonIcon';
import { useAgent } from 'providers/context/AgentProvider';
import { useBusiness } from 'providers/context/BusinessProvider';
import { useEffect, useState } from 'react';
import { IoChevronDownSharp } from 'react-icons/io5';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as urls from 'routes/config';
import { CacheKeys } from 'utils/cacheKeys';

export default function BusinessDropdown() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isAgencyView = location.pathname.includes('agency');
  const [page, setPage] = useState(1);
  const [businessList, setBusinessList] = useState<BusinessProfileResponse[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = useState<null | number>(null);
  const open = Boolean(anchorEl);
  const { setSelectedBusiness, setSelectedWorkspace } = useBusiness();
  const { setSelectedAgentId } = useAgent();
  const { data: currentUser, isLoading } = useGetCurrentUser();

  const { data: businesses } = useGetBusinesses(
    { page: page, filter: { account_status_eq: true } },
    {
      queryKey: ['businesses', page],
      enabled: Boolean(currentUser?.agency_user && !isLoading)
    }
  );

  useEffect(() => {
    if (businesses?.data) {
      setBusinessList((prev) => {
        const ids = new Set(prev.map((b) => b.id));
        return [...prev, ...businesses.data.filter((b) => !ids.has(b.id))];
      });
    }
  }, [businesses]);

  useEffect(() => {
    const storedBusinessId = localStorage.getItem(CacheKeys.businessIds);
    if (storedBusinessId && businesses?.data) {
      const businessIndex = businesses.data.findIndex((biz) => biz.id === JSON.parse(storedBusinessId)[0]);
      if (businessIndex >= 0) {
        setSelectedIndex(businessIndex);
      }
    }
  }, [businesses]);

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (_: React.MouseEvent<HTMLElement>, index: number) => {
    const selectedBusiness = businesses?.data[index];
    if (selectedBusiness) {
      localStorage.clear();
      setSelectedAgentId(undefined);
      setSelectedBusiness(Number(selectedBusiness.id));
      setSelectedWorkspace(Number(selectedBusiness?.workspaces?.[0].id));
      localStorage.setItem(CacheKeys.businessIds, JSON.stringify([selectedBusiness.id]));
      localStorage.setItem(
        CacheKeys.workspaceIds,
        JSON.stringify(selectedBusiness?.workspaces?.map((item) => item.id))
      );
      navigate('/');
      setSelectedIndex(index);
    }
    setAnchorEl(null);
  };

  const handleGoBackToAgencyView = () => {
    setSelectedBusiness(null);
    setSelectedWorkspace(null);
    localStorage.removeItem(CacheKeys.businessIds);
    localStorage.removeItem(CacheKeys.workspaceIds);

    setSelectedIndex(null);
    setAnchorEl(null);
  };

  return (
    <>
      <List component='nav' aria-label='Device settings'>
        <ListItemButton
          id='lock-button'
          aria-haspopup='listbox'
          aria-controls='lock-menu'
          aria-label='when device is locked'
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClickListItem}
          sx={{
            display: 'block',
            py: 0,
            width: {
              xs: 180,
              md: 250
            }
          }}
        >
          <Box display='flex' alignItems='center' gap={1}>
            <ListItemText>
              <Typography variant='h6' noWrap fontSize={16} fontWeight={600}>
                {!isAgencyView && selectedIndex !== null && businesses?.data?.[selectedIndex]
                  ? businesses.data[selectedIndex].name
                  : 'Click here to switch'}
              </Typography>
            </ListItemText>
            <IoChevronDownSharp />
          </Box>
        </ListItemButton>
      </List>
      <Menu
        id='lock-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { width: 250, maxHeight: 600, overflowY: 'auto' },
          onScroll: (e: any) => {
            const target = e.currentTarget;
            const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 100;
            const hasMore = Boolean((businesses?.meta?.total_pages as number) > page);
            if (nearBottom && hasMore) {
              setPage((prev) => prev + 1);
            }
          }
        }}
        MenuListProps={{
          'aria-labelledby': 'lock-button',
          role: 'listbox'
        }}
      >
        <Box position='sticky' top={0} zIndex={100} bgcolor='#fff'>
          <Box
            display='flex'
            gap={1}
            alignItems='center'
            borderBottom='2px solid #F3F3F3'
            color={theme.palette.secondary.main}
            mx={1}
            py={1}
            component={Link}
            to={urls.agency.base}
            onClick={handleGoBackToAgencyView}
          >
            <BackButtonIcon />
            <Typography variant='body1' color='primary'>
              Switch to agency view
            </Typography>
          </Box>
          <Typography variant='h3' pl={1.5} py={0.8} color='secondary'>
            All Businesses
          </Typography>
        </Box>
        {businessList.map((option, index) => (
          <MenuItem
            key={option.id}
            selected={index === selectedIndex && !location.pathname.includes('agency')}
            onClick={(event) => handleMenuItemClick(event, index)}
          >
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
              <Typography noWrap>{option.name}</Typography>
              <Typography fontSize={14} color='secondary' noWrap title={option.email}>
                ({option.email})
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
